import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, SpeakerHigh, SpeakerSlash, ArrowCounterClockwise, SkipForward } from "@phosphor-icons/react";
import Avatar2D from "@/components/Avatar2D";
import { FEET, VIEWBOX } from "@/lib/avatar2d";
import { characterFor } from "@/lib/avatarConfig";
import {
  STAGE_W, STAGE_H, ROWS, COLS, CELL, PITCH, BOARD_X, BOARD_Y,
  MY_BASE, OPP_BASE, POW, HAND_X, HAND_Y, HAND_TILE, HAND_STEP, BACK_BTN, GREEN_BTN, GREY_BTN, PLAY_BTN,
  START_CELLS, SHIELD, CONNECTOR, TIMELINE, stateAt, nextCell, cellCenter, inBase,
} from "@/lib/explainerScript";
import { playExplainerSound, setExplainerMuted, unlockExplainerAudio } from "@/lib/explainerSounds";

/**
 * "How to play" video: an animated walkthrough of a Jangle game with sound,
 * drawn live in the page (not a video file) so it stays sharp on any screen
 * and in the app. Shown at the top of How to play and before a player's
 * first game.
 */

const MUTE_KEY = "jangle.explainer.muted";
const PINK = "#f393a1";
const PINK_D = "#d9677a";
const BLUE = "#4FB0F0";
const BLUE_D = "#1F8FDB";
const PURPLE = "#9b30ff";
const POW_GREEN = "#98bf86";
const POW_GREY = "#969292";

const CSS = `
.hv-stage{position:absolute;left:0;top:0;width:${STAGE_W}px;height:${STAGE_H}px;transform-origin:0 0;
  background:radial-gradient(120% 90% at 70% 0%,#1b1f2e 0%,#0b0d14 70%);color:#fff;overflow:hidden;
  font-family:Outfit,ui-sans-serif,system-ui,sans-serif;user-select:none}
.hv-board{position:absolute;left:${BOARD_X - 6}px;top:${BOARD_Y - 6}px;width:${COLS * PITCH + 10}px;height:${ROWS * PITCH + 10}px;
  background:#252120;border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.5)}
.hv-cell{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;background:#3a3532}
.hv-start{box-shadow:inset 0 0 0 2px ${PINK_D}}
.hv-startwave{animation:hv-startwave .5s ease both}
@keyframes hv-startwave{0%{transform:scale(1)}40%{transform:scale(1.18);box-shadow:inset 0 0 0 3px #fff,0 0 14px ${PINK}}100%{transform:none}}
.hv-ghost{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;display:flex;align-items:center;justify-content:center;
  font-weight:800;font-size:15px;color:#fff;background:rgba(243,147,161,.38);border:2px dashed rgba(255,255,255,.7);z-index:6;
  animation:hv-pop .3s cubic-bezier(.2,1.6,.4,1) both}
.hv-reward{animation:hv-reward .8s ease-in-out infinite}
@keyframes hv-reward{50%{transform:scale(1.15);box-shadow:0 0 0 3px #facc15,0 0 18px rgba(250,204,21,.9)}}
.hv-badge{position:absolute;right:-7px;top:-8px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;
  background:#14161f;border-radius:50%;border:1px solid #555}
.hv-badge svg{width:12px;height:12px}
.hv-shieldtile{background:#555;border:2px solid #9aa0a6;padding:0;overflow:hidden}
.hv-shieldtile img{width:100%;height:100%;object-fit:cover}
.hv-target{animation:hv-target .6s ease-in-out infinite;cursor:crosshair}
@keyframes hv-target{50%{box-shadow:0 0 0 3px #ff5a36,0 0 16px rgba(255,90,54,.9)}}
.hv-boom{position:absolute;width:80px;height:80px;margin:-40px 0 0 -40px;border-radius:50%;pointer-events:none;z-index:12;
  background:radial-gradient(circle,#fff6b0 0%,#ffb020 35%,#ff5a1f 60%,rgba(255,60,20,0) 72%);animation:hv-boom .7s ease-out both}
@keyframes hv-boom{from{transform:scale(.2);opacity:1}to{transform:scale(1.6);opacity:0}}
.hv-spark{position:absolute;width:6px;height:6px;border-radius:50%;background:#ffcf4a;z-index:12;pointer-events:none;animation:hv-spark .6s ease-out both}
@keyframes hv-spark{to{transform:translate(var(--sx),var(--sy));opacity:0}}
.hv-opptry{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;border:2px dashed ${BLUE};z-index:9;pointer-events:none;
  display:flex;align-items:center;justify-content:center;color:#ff4444;font-size:26px;font-weight:900;animation:hv-shake .4s 2}
.hv-card{font-size:16px;gap:4px;border:2px solid transparent;opacity:.55}
.hv-card svg{width:26px;height:26px}
.hv-card.hv-green{background:${POW_GREEN};color:#163a14}
.hv-card.hv-grey{background:${POW_GREY};color:#2e2a2a}
.hv-card.hv-has{opacity:1;border-color:#fff}
.hv-card.hv-ready{animation:hv-pow .7s ease-in-out 3}
.hv-card.hv-active{box-shadow:0 0 0 3px #ff5a36,0 0 18px rgba(255,90,54,.8)}
.hv-card.hv-shakeit{animation:hv-shake .35s 2}
.hv-lock{position:absolute;right:-8px;top:-10px;width:22px;height:22px;border-radius:50%;background:#14161f;border:1.5px solid #9aa0a6;
  display:flex;align-items:center;justify-content:center;font-size:12px}
.hv-unlock{animation:hv-unlock .9s ease-out both}
@keyframes hv-unlock{0%{transform:none;opacity:1}40%{transform:translateY(-6px) rotate(-20deg) scale(1.3);opacity:1}100%{transform:translateY(-20px) rotate(-30deg);opacity:0}}
.hv-hammers{position:absolute;inset:0;z-index:28;display:flex;flex-direction:column;align-items:center;justify-content:center;
  background:#0b0d14;animation:hv-fade .35s ease both}
.hv-hcol{width:270px;border-radius:18px;padding:18px 18px 16px;text-align:center;background:#161a26;border:1px solid #2b3142;animation:hv-pop .4s cubic-bezier(.2,1.6,.4,1) both}
.hv-hsq{width:76px;height:76px;border-radius:10px;margin:0 auto 12px;display:flex;align-items:center;justify-content:center;position:relative}
.hv-hsq svg{width:64px;height:64px}
.hv-hname{font-size:22px;font-weight:900;letter-spacing:.04em}
.hv-hbody{font-size:16px;line-height:1.35;color:#cfd3dc;margin-top:6px}
.hv-harrow{font-size:17px;font-weight:800;color:#facc15;margin-top:18px;animation:hv-fade .4s ease both}
.hv-base{position:absolute;width:${3 * PITCH - 2}px;height:${3 * PITCH - 2}px;border-radius:4px;display:flex;align-items:center;justify-content:center}
.hv-pulse{animation:hv-pulse 1.1s ease-in-out infinite}
@keyframes hv-pulse{50%{box-shadow:0 0 0 4px rgba(255,255,255,.55),0 0 26px rgba(255,255,255,.35)}}
.hv-tile{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;display:flex;align-items:center;justify-content:center;
  font-weight:800;font-size:15px;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.45);transition:box-shadow .25s,border-color .25s,filter .25s}
.hv-me{background:${PINK};border:2px solid ${PINK_D};box-shadow:0 0 10px rgba(243,147,161,.55)}
.hv-opp{background:${BLUE};border:2px solid ${BLUE_D};box-shadow:0 0 10px rgba(79,176,240,.55)}
.hv-locked{filter:saturate(.9)}
.hv-open{box-shadow:0 0 0 2px #fff,0 0 14px rgba(243,147,161,.8)}
.hv-good{border-color:#22c55e!important;box-shadow:0 0 0 2px #22c55e,0 0 16px rgba(34,197,94,.75)!important}
.hv-bad{border-color:#ff4444!important;box-shadow:0 0 0 2px #ff4444,0 0 16px rgba(255,68,68,.75)!important;animation:hv-shake .35s}
@keyframes hv-shake{25%{transform:translateX(-3px)}75%{transform:translateX(3px)}}
.hv-fly{animation:hv-fly .32s cubic-bezier(.2,.8,.3,1.2) both}
@keyframes hv-fly{from{transform:translate(var(--dx),var(--dy)) scale(1.25);opacity:.7}to{transform:none;opacity:1}}
.hv-pop{animation:hv-pop .3s cubic-bezier(.2,1.6,.4,1) both}
@keyframes hv-pop{from{transform:scale(.3);opacity:0}to{transform:none;opacity:1}}
.hv-anchor{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;outline:2.5px solid ${PURPLE};outline-offset:-2px;
  box-shadow:0 0 12px rgba(155,48,255,.6);pointer-events:none}
.hv-next{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;border:2px dashed ${PURPLE};pointer-events:none;
  transition:left .18s ease,top .18s ease;animation:hv-blink 1s ease-in-out infinite}
@keyframes hv-blink{50%{border-color:rgba(155,48,255,.45)}}
.hv-dir{position:absolute;color:${PURPLE};font-size:14px;font-weight:900;pointer-events:none;text-shadow:0 0 6px rgba(155,48,255,.8)}
.hv-gem.hv-gem-pulse svg{animation:hv-gem 0.8s ease-in-out infinite}
@keyframes hv-gem{50%{transform:scale(1.3);filter:drop-shadow(0 0 6px #c084fc)}}
.hv-powpulse{animation:hv-pow .7s ease-in-out infinite}
@keyframes hv-pow{50%{transform:scale(1.15);box-shadow:0 0 18px rgba(170,255,150,.8)}}
.hv-panel{position:absolute;left:340px;top:28px;width:350px}
.hv-step{display:inline-block;font-size:11px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;color:#f8a3b0;
  background:rgba(243,147,161,.12);border:1px solid rgba(243,147,161,.35);padding:4px 10px;border-radius:999px}
.hv-title{font-size:31px;font-weight:900;letter-spacing:-.02em;line-height:1.08;margin-top:12px}
.hv-text{font-size:19px;line-height:1.38;color:#cfd3dc;margin-top:10px}
.hv-fade{animation:hv-fade .35s ease both}
@keyframes hv-fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.hv-chip{position:absolute;left:340px;top:258px;font-size:15px;font-weight:900;letter-spacing:.06em;padding:5px 12px;border-radius:999px}
.hv-label{position:absolute;left:${HAND_X}px;top:${HAND_Y - 20}px;font-size:10px;font-weight:800;letter-spacing:.2em;color:#8a90a0}
.hv-hand{position:absolute;width:${HAND_TILE}px;height:${HAND_TILE}px;border-radius:8px;display:flex;align-items:center;justify-content:center;
  font-size:18px;font-weight:800;color:#fff;background:${BLUE};border:2px solid ${BLUE_D};box-shadow:0 3px 0 #1670ad;
  transition:left .25s ease,transform .12s}
.hv-hand img{width:100%;height:100%;border-radius:6px;object-fit:cover}
.hv-hand.hv-shield{background:#555;border-color:#888;box-shadow:0 3px 0 #333;padding:0}
.hv-hand.hv-conn svg{width:30px;height:30px}
.hv-tile svg.hv-connic{width:24px;height:24px}
.hv-gemfly{position:absolute;z-index:22;pointer-events:none;margin:-15px 0 0 -15px;animation:hv-gemfly .8s cubic-bezier(.5,0,.3,1) both}
.hv-gemfly svg{width:30px;height:30px;filter:drop-shadow(0 0 10px #c084fc)}
@keyframes hv-gemfly{0%{transform:translate(0,0) scale(1)}50%{transform:translate(calc(var(--dx) * .5),calc(var(--dy) * .5 - 50px)) scale(1.8)}100%{transform:translate(var(--dx),var(--dy)) scale(1)}}
.hv-hole{position:absolute;width:${CELL}px;height:${CELL}px;border-radius:4px;border:2px dashed #facc15;z-index:5;pointer-events:none;animation:hv-target .6s ease-in-out infinite}
.hv-hand.hv-shieldpulse{animation:hv-shieldpulse .8s ease-in-out infinite}
@keyframes hv-shieldpulse{50%{transform:translateY(-4px);box-shadow:0 0 18px #facc15}}
.hv-wave{animation:hv-wave .45s ease both}
@keyframes hv-wave{0%{transform:translateY(0)}40%{transform:translateY(-10px)}100%{transform:none}}
.hv-btn{position:absolute;border-radius:999px;display:flex;align-items:center;justify-content:center;font-weight:800;transition:transform .12s}
.hv-play{background:#e5735a;color:#fff;font-size:15px;letter-spacing:.08em;box-shadow:0 3px 0 #a94a35}
.hv-back{background:#262a36;color:#fff;font-size:20px;border:1px solid #3b4050}
.hv-press{transform:scale(.92)}
.hv-toast{position:absolute;left:${BOARD_X + (COLS * PITCH) / 2}px;top:${BOARD_Y + 4 * PITCH}px;transform:translateX(-50%);
  background:rgba(10,12,18,.92);border:1px solid #22c55e;color:#22c55e;font-weight:900;font-size:16px;padding:8px 16px;border-radius:12px;
  white-space:nowrap;animation:hv-pop .3s cubic-bezier(.2,1.6,.4,1) both;z-index:5}
.hv-finger{position:absolute;width:52px;height:52px;margin-left:-14px;margin-top:-4px;pointer-events:none;z-index:20;
  transition:left .42s cubic-bezier(.4,0,.2,1),top .42s cubic-bezier(.4,0,.2,1),opacity .3s}
.hv-finger svg{filter:drop-shadow(0 4px 6px rgba(0,0,0,.55))}
.hv-finger.hv-down svg{transform:scale(.88) translateY(3px);transition:transform .08s}
.hv-ripple{position:absolute;width:44px;height:44px;margin:-22px 0 0 -22px;border-radius:50%;border:3px solid rgba(255,255,255,.9);
  pointer-events:none;z-index:19;animation:hv-ripple .5s ease-out forwards}
@keyframes hv-ripple{from{transform:scale(.3);opacity:1}to{transform:scale(1.3);opacity:0}}
.hv-raider{position:absolute;pointer-events:none;z-index:8;transition:left .23s linear,top .23s linear}
.hv-overlay{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;z-index:30;
  background:radial-gradient(90% 90% at 50% 40%,rgba(30,34,52,.97),rgba(8,10,16,.98))}
.hv-logo{width:380px;height:auto;filter:drop-shadow(0 6px 10px rgba(0,0,0,.35));animation:hv-drop .5s cubic-bezier(.2,1.5,.4,1) both}
@keyframes hv-drop{from{transform:translateY(-60px) rotate(-12deg);opacity:0}to{transform:none;opacity:1}}
.hv-confetti{position:absolute;top:-12px;width:8px;height:12px;border-radius:2px;z-index:25;animation:hv-fall 2.4s linear both}
@keyframes hv-fall{to{transform:translateY(${STAGE_H + 30}px) rotate(540deg)}}
@media (prefers-reduced-motion: reduce){.hv-stage *{animation-duration:.01ms!important;transition-duration:.01ms!important}}
`;

const FlagIcon = ({ flip }) => (
  <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden style={flip ? { transform: "scaleX(-1)" } : undefined}>
    <path d="M6 22V3" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M6.5 4c4-2 7 2 11 0v8c-4 2-7-2-11 0z" fill="#fff" />
  </svg>
);

// The hammer the game draws on Pow squares and cards (POW_HAMMER_INNER in
// public/game/jangle.html).
const Hammer = ({ size = 22 }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden>
    <g transform="translate(62 66) rotate(-38)" strokeLinejoin="round" strokeLinecap="round">
      <rect x="-6" y="-26" width="12" height="76" rx="5" fill="#8a5a2b" stroke="#2b1f2a" strokeWidth="6" />
      <path d="M-2 -10 v52" stroke="#b07a45" strokeWidth="3" />
      <rect x="-34" y="-56" width="68" height="40" rx="8" fill="#9aa3ad" stroke="#2b1f2a" strokeWidth="6" />
      <path d="M6 -53 h20 q6 0 6 6 v22 q0 6 -6 6 h-20z" fill="#000" opacity=".15" />
      <path d="M-26 -48 h22" stroke="#fff" strokeWidth="4" opacity=".6" />
      <rect x="-37" y="-41" width="74" height="10" rx="4" fill="#f2c94c" stroke="#2b1f2a" strokeWidth="4.5" />
    </g>
  </svg>
);

const Gem = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
    <path d="M12 2l7 10-7 10-7-10z" fill="#a855f7" stroke="#e9d5ff" strokeWidth="1" />
    <path d="M12 2l3 10-3 10-3-10z" fill="#c084fc" opacity=".8" />
  </svg>
);

// Connector Tile artwork from the game (connectorSvg in public/game/jangle.html).
const ConnectorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 100 100" aria-label="Connector tile">
    <path
      fill="currentColor" fillOpacity=".75" fillRule="evenodd"
      d="M41.5 14H58.5A2 2 0 0 1 60.5 16V26.21A26 26 0 0 1 73.79 39.5H84A2 2 0 0 1 86 41.5V58.5A2 2 0 0 1 84 60.5H73.79A26 26 0 0 1 60.5 73.79V84A2 2 0 0 1 58.5 86H41.5A2 2 0 0 1 39.5 84V73.79A26 26 0 0 1 26.21 60.5H16A2 2 0 0 1 14 58.5V41.5A2 2 0 0 1 16 39.5H26.21A26 26 0 0 1 39.5 26.21V16A2 2 0 0 1 41.5 14ZM50 40.5A9.5 9.5 0 1 0 50 59.5A9.5 9.5 0 1 0 50 40.5Z"
    />
  </svg>
);

const Finger = () => (
  <svg viewBox="0 0 52 52" width="52" height="52" aria-hidden>
    <path
      d="M14 6a4 4 0 0 1 8 0v15l1-.5a4 4 0 0 1 4.5 1.2 4 4 0 0 1 5.5 1.3 4 4 0 0 1 6.5 3V36c0 7-5 13-12 13h-3c-5 0-8-2-11-6l-7-9a3.6 3.6 0 0 1 5.4-4.8L14 31z"
      fill="#fff" stroke="#1d2230" strokeWidth="2" strokeLinejoin="round"
    />
  </svg>
);

const at = (r, c) => ({ left: BOARD_X + c * PITCH, top: BOARD_Y + r * PITCH });
const ARROWS = { "-1,0": "↑", "0,1": "→", "1,0": "↓", "0,-1": "←" };

function fmt(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function Stage({ s, now, character, narrow }) {
  const n = nextCell(s);
  const next = n && n.r >= 0 && n.r < ROWS && n.c >= 0 && n.c < COLS ? n : null;
  const recent = (t, ms) => t != null && now - t >= 0 && now - t < ms;

  const cells = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (inBase(MY_BASE, r, c) || inBase(OPP_BASE, r, c)) continue;
      const k = `${r},${c}`;
      const pow = POW[k];
      const startIdx = s.showStarts ? START_CELLS.indexOf(k) : -1;
      const isStart = startIdx >= 0;
      const waving = isStart && recent(s.startWave, 110 * START_CELLS.length + 600);
      cells.push(
        <div
          key={k}
          className={`hv-cell${isStart ? " hv-start" : ""}${waving ? " hv-startwave" : ""}`}
          style={{
            ...at(r, c),
            ...(waving ? { animationDelay: `${startIdx * 110}ms` } : null),
            ...(pow ? { background: pow === "green" ? POW_GREEN : POW_GREY, display: "flex", alignItems: "center", justifyContent: "center" } : null),
          }}
        >
          {pow && <Hammer size={24} />}
          {s.gems.includes(k) && (
            <div className={`hv-gem${s.gemPulse && k === "6,6" ? " hv-gem-pulse" : ""}`} style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}>
              <Gem />
            </div>
          )}
        </div>,
      );
    }
  }

  const base = (b, colour, flip, taken) => (
    <div
      className={`hv-base${s.goal ? " hv-pulse" : ""}`}
      style={{ ...at(b.r0, b.c0), background: colour }}
    >
      {!taken && <FlagIcon flip={flip} />}
    </div>
  );

  const raiderW = 54;
  const raiderH = raiderW * (VIEWBOX.h / VIEWBOX.w);

  return (
    <>
      <div className="hv-board" />
      {cells}
      {base(OPP_BASE, BLUE, false, s.flagTaken)}
      {base(MY_BASE, PINK, true, false)}

      {s.goal && (
        <svg style={{ position: "absolute", left: 0, top: 0, zIndex: 4, pointerEvents: "none" }} width={STAGE_W} height={STAGE_H}>
          <defs>
            <marker id="hv-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M0 0L10 5L0 10z" fill="#fff" />
            </marker>
          </defs>
          <path
            d={`M${cellCenter(9, 4).x} ${cellCenter(9, 4).y} L${cellCenter(3, 4).x} ${cellCenter(3, 4).y}`}
            stroke="#fff" strokeWidth="4" strokeDasharray="8 8" fill="none" markerEnd="url(#hv-arrow)"
            style={{ animation: "hv-dash 0.6s linear infinite" }}
          />
          <style>{"@keyframes hv-dash{to{stroke-dashoffset:-16}}"}</style>
        </svg>
      )}

      {Object.entries(s.tiles).map(([k, t]) => {
        const [r, c] = k.split(",").map(Number);
        const pos = at(r, c);
        const anim = recent(t.born, 500);
        const center = cellCenter(r, c);
        const fly = anim && t.from;
        return (
          <div
            key={k}
            className={[
              "hv-tile",
              t.who === "me" ? "hv-me" : "hv-opp",
              t.locked ? "hv-locked" : t.who === "me" ? "hv-open" : "",
              t.st === "good" ? "hv-good" : t.st === "bad" ? "hv-bad" : "",
              fly ? "hv-fly" : anim ? "hv-pop" : "",
              t.l === SHIELD ? "hv-shieldtile" : "",
              s.cutMode && t.who === "opp" ? "hv-target" : "",
              (s.gemPulse && s.gems.includes(k)) || (s.powPulse && POW[k]) ? "hv-reward" : "",
            ].join(" ")}
            style={{
              ...pos,
              zIndex: 6,
              ...(fly ? { "--dx": `${t.from.x - center.x}px`, "--dy": `${t.from.y - center.y}px` } : null),
            }}
          >
            {t.l === SHIELD ? <img src="/game/shield-tile-steel.png" alt="" /> : t.l === CONNECTOR ? <ConnectorIcon className="hv-connic" /> : t.l}
            {t.who === "me" && !t.locked && (s.gems.includes(k) || POW[k]) && (
              <span className="hv-badge" style={s.gems.includes(k) ? undefined : { background: POW[k] === "green" ? POW_GREEN : POW_GREY }}>
                {s.gems.includes(k) ? <Gem /> : <Hammer size={14} />}
              </span>
            )}
          </div>
        );
      })}

      {s.ghosts.map((g) => (
        <div key={`${g.r},${g.c}`} className="hv-ghost" style={at(g.r, g.c)}>{g.l}</div>
      ))}

      {s.hole && <div className="hv-hole" style={at(s.hole.r, s.hole.c)} />}

      {s.gemFly && (
        <div
          className="hv-gemfly"
          key={`gemfly-${s.gemFly.t}`}
          style={{ left: s.gemFly.from.x, top: s.gemFly.from.y, "--dx": `${s.gemFly.to.x - s.gemFly.from.x}px`, "--dy": `${s.gemFly.to.y - s.gemFly.from.y}px` }}
        >
          <Gem />
        </div>
      )}

      {s.oppTry && <div className="hv-opptry" style={at(s.oppTry.r, s.oppTry.c)}>✕</div>}

      {s.boom && recent(s.boom.t, 800) && (
        <>
          <div className="hv-boom" style={{ left: cellCenter(s.boom.r, s.boom.c).x, top: cellCenter(s.boom.r, s.boom.c).y }} />
          {Array.from({ length: 10 }, (_, i) => {
            const a = (i / 10) * Math.PI * 2;
            return (
              <div
                key={i}
                className="hv-spark"
                style={{
                  left: cellCenter(s.boom.r, s.boom.c).x - 3,
                  top: cellCenter(s.boom.r, s.boom.c).y - 3,
                  "--sx": `${Math.cos(a) * 46}px`,
                  "--sy": `${Math.sin(a) * 46}px`,
                }}
              />
            );
          })}
        </>
      )}

      {s.anchor && <div className="hv-anchor" style={{ ...at(s.anchor.r, s.anchor.c), zIndex: 7 }} />}
      {next && (
        <>
          <div className="hv-next" style={{ ...at(next.r, next.c), zIndex: 7 }} />
          {!s.placed.length && s.anchor && (
            <div
              className="hv-dir"
              style={{
                left: cellCenter(s.anchor.r, s.anchor.c).x + s.dir[1] * 22 - 5,
                top: cellCenter(s.anchor.r, s.anchor.c).y + s.dir[0] * 22 - 10,
                zIndex: 8,
              }}
            >
              {ARROWS[s.dir.join(",")]}
            </div>
          )}
        </>
      )}

      {s.raider && (
        <div
          className="hv-raider"
          style={{
            width: raiderW,
            height: raiderH,
            left: cellCenter(s.raider.r, s.raider.c).x - FEET.x * raiderW,
            top: cellCenter(s.raider.r, s.raider.c).y + 10 - FEET.y * raiderH,
          }}
        >
          <Avatar2D character={character} pose={s.raider.pose} flag={s.raider.flag} mirror={s.raider.face < 0} className="w-full h-full" />
        </div>
      )}

      {s.toast && (
        <div
          className="hv-toast"
          key={`toast-${s.toast.t}`}
          style={s.toast.kind === "bad" ? { borderColor: "#ff4444", color: "#ff6b6b" } : undefined}
        >
          {s.toast.text}
        </div>
      )}

      {s.caption && (
        <div className="hv-panel" key={s.caption.title}>
          <div className="hv-fade">
            <span className="hv-step" style={narrow ? { fontSize: 18, padding: "6px 14px" } : undefined}>{s.caption.step}</span>
            <div className="hv-title" style={narrow ? { fontSize: 44, marginTop: 16 } : undefined}>{s.caption.title}</div>
          </div>
          {!narrow && <div className="hv-text hv-fade" key={s.caption.text}>{s.caption.text}</div>}
        </div>
      )}
      {s.chip && (
        <div
          className="hv-chip hv-pop"
          key={`chip-${s.chip.t}`}
          style={s.chip.st === "good"
            ? { background: "rgba(34,197,94,.15)", color: "#4ade80", border: "1px solid #22c55e" }
            : { background: "rgba(255,68,68,.15)", color: "#ff6b6b", border: "1px solid #ff4444" }}
        >
          {s.chip.text}
        </div>
      )}

      <div className="hv-label">YOUR HAND</div>
      {s.hand.map((h, i) => {
        const wave = recent(s.handWave, 900);
        const shield = h.l === SHIELD;
        const conn = h.l === CONNECTOR;
        const pulse = (shield && s.shieldPulse) || (conn && s.connPulse);
        return (
          <div
            key={h.id}
            className={[
              "hv-hand",
              shield ? "hv-shield" : "",
              conn ? "hv-conn" : "",
              pulse ? "hv-shieldpulse" : "",
              wave && !pulse ? "hv-wave" : "",
            ].join(" ")}
            style={{ left: HAND_X + i * HAND_STEP, top: HAND_Y, animationDelay: wave ? `${i * 50}ms` : undefined }}
          >
            {shield ? <img src="/game/shield-tile-steel.png" alt="Wall tile" /> : conn ? <ConnectorIcon /> : h.l}
          </div>
        );
      })}
      <div
        className={`hv-btn hv-back${s.pressed === "back" && recent(s.pressedAt, 220) ? " hv-press" : ""}`}
        style={{ left: BACK_BTN.x, top: BACK_BTN.y, width: BACK_BTN.w, height: BACK_BTN.h }}
      >
        ⌫
      </div>
      {[["green", GREEN_BTN, s.green], ["grey", GREY_BTN, s.grey]].map(([kind, b, n]) => (
        <div
          key={kind}
          className={[
            "hv-btn hv-card",
            `hv-${kind}`,
            n > 0 ? "hv-has" : "",
            s.cardReady === kind ? "hv-ready" : "",
            s.cutMode === kind ? "hv-active" : "",
            kind === "grey" && recent(s.lockShake, 800) ? "hv-shakeit" : "",
            s.pressed === kind && recent(s.pressedAt, 220) ? "hv-press" : "",
          ].join(" ")}
          style={{ left: b.x, top: b.y, width: b.w, height: b.h }}
        >
          <Hammer /> {n}
          {kind === "grey" && (s.greyLocked || recent(s.unlockAt, 900)) && (
            <span className={`hv-lock${s.greyLocked ? "" : " hv-unlock"}`}>{s.greyLocked ? "🔒" : "🔓"}</span>
          )}
        </div>
      ))}
      <div
        className={`hv-btn hv-play${s.pressed === "play" && recent(s.pressedAt, 220) ? " hv-press" : ""}`}
        style={{ left: PLAY_BTN.x, top: PLAY_BTN.y, width: PLAY_BTN.w, height: PLAY_BTN.h }}
      >
        ✓ PLAY
      </div>

      {recent(s.finger.tap, 500) && (
        <div className="hv-ripple" key={`ripple-${s.finger.tap}`} style={{ left: s.finger.x, top: s.finger.y }} />
      )}
      <div
        className={`hv-finger${recent(s.finger.tap, 140) ? " hv-down" : ""}`}
        style={{ left: s.finger.x, top: s.finger.y, opacity: s.finger.show ? 1 : 0 }}
      >
        <Finger />
      </div>

      {s.hammers && (
        <div className="hv-hammers">
          <div style={{ fontSize: 30, fontWeight: 900, marginBottom: 18 }}>Green vs grey hammers</div>
          <div style={{ display: "flex", gap: 22 }}>
            <div className="hv-hcol" style={{ borderColor: "#7f9a76" }}>
              <div className="hv-hsq" style={{ background: POW_GREEN }}><Hammer size={64} /></div>
              <div className="hv-hname" style={{ color: "#b9d6ae" }}>GREEN HAMMER</div>
              <div className="hv-hbody">Green squares in the corners. Works straight away.</div>
            </div>
            {s.hammers.step >= 2 && (
              <div className="hv-hcol" style={{ borderColor: "#8a8686" }}>
                <div className="hv-hsq" style={{ background: POW_GREY }}>
                  <Hammer size={64} />
                  <span className={`hv-lock${s.hammers.step >= 3 ? " hv-unlock" : ""}`} style={{ width: 30, height: 30, fontSize: 16, right: -10, top: -10 }}>
                    {s.hammers.step >= 3 ? "🔓" : "🔒"}
                  </span>
                </div>
                <div className="hv-hname" style={{ color: "#d6d2d2" }}>GREY HAMMER</div>
                <div className="hv-hbody">Grey squares on the middle row. Locked until you've used a green hammer.</div>
              </div>
            )}
          </div>
          {s.hammers.step >= 3 && <div className="hv-harrow" style={{ maxWidth: 600, textAlign: "center" }}>Use a green hammer → your grey ones unlock. Each hammer smashes one of their tiles.</div>}
        </div>
      )}

      {s.confetti && Array.from({ length: 36 }, (_, i) => (
        <div
          key={i}
          className="hv-confetti"
          style={{
            left: (i * 97) % STAGE_W,
            background: ["#f393a1", "#4FB0F0", "#facc15", "#22c55e", "#c084fc"][i % 5],
            animationDelay: `${(i % 9) * 0.12}s`,
          }}
        />
      ))}
    </>
  );
}

export default function HowToPlayVideo({ character, onFinish, finishLabel = "Let's play", onSkip, className = "" }) {
  const hero = useMemo(() => character || characterFor("guest"), [character]);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(0);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
  });
  const timeRef = useRef(0);
  const playingRef = useRef(false);
  const { events, chapters, duration } = TIMELINE;

  // Fit the fixed-size stage to the width we're given, like a video.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const fit = () => setScale(el.clientWidth / STAGE_W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setExplainerMuted(muted); }, [muted]);

  // Playback loop: advance the clock and fire the sounds of events it passes.
  useEffect(() => {
    playingRef.current = playing;
    if (!playing) return undefined;
    let raf;
    let last = performance.now();
    const tick = (ts) => {
      const prev = timeRef.current;
      const t = Math.min(duration, prev + (ts - last));
      last = ts;
      for (const e of events) {
        if (e.t > t) break;
        if (e.t > prev && e.sound) playExplainerSound(e.sound);
      }
      timeRef.current = t;
      setNow(t);
      if (t >= duration) { setPlaying(false); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, events, duration]);

  // Pause when the tab is hidden.
  useEffect(() => {
    const onVis = () => { if (document.hidden) setPlaying(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Rebuild the scene only when the clock crosses an event.
  const passed = useMemo(() => {
    let n = 0;
    while (n < events.length && events[n].t <= now) n++;
    return n;
  }, [now, events]);
  const scene = useMemo(() => stateAt(passed ? events[passed - 1].t : -1), [passed, events]);

  const seek = useCallback((t) => {
    timeRef.current = Math.max(0, Math.min(duration, t));
    setNow(timeRef.current);
  }, [duration]);

  const start = () => {
    unlockExplainerAudio();
    setStarted(true);
    if (timeRef.current >= duration) seek(0);
    setPlaying(true);
  };
  const toggle = () => {
    unlockExplainerAudio();
    if (playing) setPlaying(false);
    else start();
  };
  const replay = () => { unlockExplainerAudio(); seek(0); setStarted(true); setPlaying(true); };
  const nextChapter = () => {
    const c = chapters.find((ch) => ch.t > timeRef.current + 50);
    seek(c ? c.t : duration);
    if (!playing && started) setPlaying(true);
  };
  const toggleMute = () => {
    setMuted((m) => {
      try { localStorage.setItem(MUTE_KEY, m ? "0" : "1"); } catch { /* ignore */ }
      return !m;
    });
  };

  // On a phone the stage is too small to read the captions in it, so they
  // show under the picture instead.
  const narrow = scale < 0.75;
  const progress = now / duration;
  const current = [...chapters].reverse().find((c) => c.t <= now) || chapters[0];
  const ended = scene.overlay === "end";

  return (
    <div className={`rounded-2xl overflow-hidden border border-border bg-[#0b0d14] shadow-lg ${className}`} data-testid="how-to-play-video">
      <style>{CSS}</style>
      <div
        ref={wrapRef}
        className="relative w-full cursor-pointer"
        style={{ aspectRatio: `${STAGE_W} / ${STAGE_H}` }}
        onClick={ended ? undefined : toggle}
        role="button"
        tabIndex={0}
        aria-label={playing ? "Pause how to play video" : "Play how to play video"}
        onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); } }}
      >
        <div className="hv-stage" style={{ transform: `scale(${scale})` }} aria-hidden>
          <Stage s={scene} now={now} character={hero} narrow={narrow} />

          {scene.overlay === "title" && (
            <div className="hv-overlay">
              <img className="hv-logo" src="/brand/jangle-logo-640.webp" alt="" draggable={false} />
              <div style={{ fontSize: 30, fontWeight: 900, marginTop: 22 }}>How to play</div>
              <div style={{ fontSize: 17, color: "#aab0bf", marginTop: 6 }}>Learn the game in two minutes</div>
            </div>
          )}

          {ended && (
            <div className="hv-overlay hv-fade">
              <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-.02em" }}>You're ready!</div>
              <div style={{ fontSize: 18, color: "#aab0bf", marginTop: 8, maxWidth: 460 }}>
                Tap a square, tap again to turn, type real words and race to their flag.
              </div>
            </div>
          )}
        </div>

        {!started && (
          <div className="absolute inset-0 flex items-end justify-center pb-[9%] pointer-events-none">
            <div className="flex items-center gap-2 rounded-full bg-[#e5735a] text-white font-bold px-5 py-2.5 shadow-lg text-sm sm:text-base">
              <Play size={18} weight="fill" /> Watch with sound
            </div>
          </div>
        )}

        {ended && (
          <div className="absolute inset-x-0 bottom-[14%] flex justify-center gap-3" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={replay}
              className="flex items-center gap-2 rounded-full border border-white/30 text-white font-bold px-4 py-2 text-sm hover:bg-white/10"
              data-testid="how-to-play-video-replay"
            >
              <ArrowCounterClockwise size={16} weight="bold" /> Watch again
            </button>
            {onFinish && (
              <button
                type="button"
                onClick={onFinish}
                className="rounded-full bg-[#e5735a] text-white font-bold px-5 py-2 text-sm hover:brightness-110"
                data-testid="how-to-play-video-finish"
              >
                {finishLabel}
              </button>
            )}
          </div>
        )}
      </div>

      {narrow && (
        <div className="px-3 py-2.5 bg-[#0b0d14] text-[#cfd3dc] text-sm leading-snug min-h-[3.6rem]" aria-live="polite">
          {scene.caption ? scene.caption.text : "Learn the game in two minutes."}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 py-2 bg-[#11131b] text-white">
        <button type="button" onClick={toggle} className="p-1.5 rounded-full hover:bg-white/10" aria-label={playing ? "Pause" : "Play"} data-testid="how-to-play-video-toggle">
          {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
        </button>
        <div
          className="relative flex-1 h-2 rounded-full bg-white/15 cursor-pointer"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            unlockExplainerAudio();
            seek(((e.clientX - r.left) / r.width) * duration);
            setStarted(true);
          }}
          role="slider"
          aria-label="Video position"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration / 1000)}
          aria-valuenow={Math.round(now / 1000)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") seek(now + 5000);
            if (e.key === "ArrowLeft") seek(now - 5000);
          }}
        >
          <div className="absolute inset-y-0 left-0 rounded-full bg-[#e5735a]" style={{ width: `${progress * 100}%` }} />
          {chapters.slice(1).map((c) => (
            <div key={c.t} title={c.title} className="absolute top-0 bottom-0 w-0.5 bg-[#11131b]" style={{ left: `${(c.t / duration) * 100}%` }} />
          ))}
        </div>
        <span className="hidden sm:inline text-[11px] font-mono text-white/60 min-w-[92px] truncate" title={current.title}>
          {current.title}
        </span>
        <span className="text-[11px] font-mono text-white/60 tabular-nums">{fmt(now)}/{fmt(duration)}</span>
        <button type="button" onClick={nextChapter} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Next part" title="Next part">
          <SkipForward size={16} weight="fill" />
        </button>
        <button type="button" onClick={toggleMute} className="p-1.5 rounded-full hover:bg-white/10" aria-label={muted ? "Turn sound on" : "Mute"}>
          {muted ? <SpeakerSlash size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
        </button>
        {onSkip && (
          <button type="button" onClick={onSkip} className="text-xs font-bold px-2.5 py-1 rounded-full border border-white/25 hover:bg-white/10" data-testid="how-to-play-video-skip">
            Skip
          </button>
        )}
      </div>
    </div>
  );
}
